import { useEffect, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react'
import { api } from '../../lib/api.js'
import { fmtClock as fmtT } from '../../lib/date.js'
import { toast } from '../../components/feedback/Toast.jsx'
import SavedCopyNote from '../../components/SavedCopyNote.jsx'
import { askAgain } from '../../lib/savedCopy.js'
import { useOnline } from '../../lib/useOnline.js'
import { todayISO } from '../sessionlog/lib.js'

/**
 * CDM Calendar — today's bookings as a single-day timeline, Google-style:
 * one column, overlapping events share the width side by side. Each block
 * reads "Studio B - Prha - WORK IN PROGRESS" plus start + duration
 * (Charles, 2026-08-03 — room name in the event, not room columns).
 * Bare-bones by design; the calendar itself stays in Google.
 */

const HOUR_PX = 56

// How many days the pager covers, counting today.
export const RANGE_DAYS = 6

const toMin = (hhmm) => hhmm.split(':').reduce((h, m) => h * 60 + Number(m), 0)

export default function CalendarPage() {
  const qc = useQueryClient()
  const today = todayISO()
  // Rolling window: today plus the next five days, whatever day it is
  // (Charles, 2026-08-24, supersedes today→this week's Sunday — a daily
  // view has no "week"). One number to change the span.
  const rangeEnd = addDays(today, RANGE_DAYS - 1)
  const [date, setDate] = useState(today)
  const online = useOnline()
  const { data, isLoading, isError, isPaused, isFetching, isPlaceholderData } = useQuery({
    queryKey: ['cal-day', date],
    queryFn: () => api(`/log/calendar?date=${date}`),
    staleTime: 60 * 1000,
    refetchOnWindowFocus: true,
    // Every 5 minutes — every 30 s while what's shown is the saved copy,
    // so it's replaced as soon as there's service again.
    refetchInterval: (q) => askAgain(q.state.data),
    placeholderData: (prev) => prev, // keep the grid up while paging
  })
  // Nothing to show and nothing coming: the fetch failed, or — with no
  // network at all — the query library is holding it until there is one.
  // The day still on screen from paging (placeholderData) doesn't count:
  // it's another day's.
  const missing = (!data || isPlaceholderData) && (isError || isPaused)
  const [refreshing, setRefreshing] = useState(false)

  // Finger paging (Charles, 2026-08-03), now with the Google slide
  // (Charles, 2026-08-05: "no visual feedback until it's done"): the grid
  // follows the finger while dragging (with resistance at the week's
  // edges), then slides out and the new day slides in. Direction is still
  // decided once per gesture so vertical scrolling stays untouched — the
  // card's touch-pan-y leaves vertical moves native. The transform is
  // driven through the ref, not state: no re-render per touchmove.
  const paneRef = useRef(null)
  const touch = useRef(null)
  const animating = useRef(false)

  function setX(x, animate) {
    const el = paneRef.current
    if (!el) return
    el.style.transition = animate ? 'transform 200ms ease-out' : 'none'
    el.style.transform = x ? `translateX(${x}px)` : ''
  }

  // The slide choreography, shared by swipe, arrows and Today: out, swap,
  // in. The slide is always one card-width in the travel direction, even
  // when Today jumps several days.
  function pageTo(target) {
    if (target === date || target < today || target > rangeEnd || animating.current) return
    animating.current = true
    const dir = target > date ? 1 : -1
    const w = paneRef.current?.offsetWidth || 360
    setX(-dir * w, true)
    setTimeout(() => {
      setDate(target)
      setX(dir * w, false)
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          setX(0, true)
          setTimeout(() => { animating.current = false }, 220)
        })
      )
    }, 200)
  }
  const page = (delta) => pageTo(addDays(date, delta))

  function onTouchStart(e) {
    if (animating.current) return
    const t = e.touches[0]
    touch.current = { x: t.clientX, y: t.clientY, mode: null }
  }
  function onTouchMove(e) {
    const s = touch.current
    if (!s) return
    const t = e.touches[0]
    const dx = t.clientX - s.x
    const dy = t.clientY - s.y
    if (!s.mode) {
      if (Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(dy) * 1.2) s.mode = 'h'
      else if (Math.abs(dy) > 8) s.mode = 'v'
      else return
    }
    if (s.mode !== 'h') return
    // Past the week's bounds the grid still moves, at a third — the
    // "there's nothing further" feel.
    const blocked = (dx > 0 && date <= today) || (dx < 0 && date >= rangeEnd)
    setX(blocked ? dx / 3 : dx, false)
  }
  function onTouchEnd(e) {
    const s = touch.current
    touch.current = null
    if (!s || s.mode !== 'h') return
    const dx = e.changedTouches[0].clientX - s.x
    if (dx < -60 && date < rangeEnd) page(1)
    else if (dx > 60 && date > today) page(-1)
    else setX(0, true) // not far enough, or at a bound — snap back
  }

  // The day about to slide in shouldn't slide in empty: keep the
  // neighbours warm in the query cache.
  useEffect(() => {
    for (const d of [addDays(date, 1), addDays(date, -1)]) {
      if (d >= today && d <= rangeEnd) {
        qc.prefetchQuery({
          queryKey: ['cal-day', d],
          queryFn: () => api(`/log/calendar?date=${d}`),
          staleTime: 60 * 1000,
        })
      }
    }
  }, [date, today, rangeEnd, qc])

  // Same two-layer refresh as the Session Log suggestions: the tap
  // bypasses the server's ICS cache and really re-reads Google.
  async function refresh() {
    setRefreshing(true)
    try {
      qc.setQueryData(['cal-day', date], await api(`/log/calendar?date=${date}&fresh=1`))
    } catch {
      toast('Couldn’t reach the calendar', 'error')
    } finally {
      setRefreshing(false)
    }
  }

  const events = layout(data?.events || [])
  // Business hours by default; stretched when a booking falls outside.
  const startHour = Math.min(8, ...events.map((e) => Math.floor(e.s / 60)))
  const endHour = Math.max(20, ...events.map((e) => Math.ceil(e.e2 / 60)))
  const totalPx = (endHour - startHour) * HOUR_PX
  const y = (min) => ((min - startHour * 60) / 60) * HOUR_PX

  const hours = []
  for (let h = startHour; h <= endHour; h++) hours.push(h)

  // Google's red "now" line, in cyan — red stays armed-only here.
  const now = new Date()
  const nowMin = now.getHours() * 60 + now.getMinutes()
  const showNow = date === today && nowMin > startHour * 60 && nowMin < endHour * 60

  return (
    // One day reads as a column, on a desktop too (Charles, 2026-10-04:
    // "make the calendar like it is on the phone for the desktop view") —
    // the same width Session Builder's day view keeps, instead of blocks
    // stretched across the whole screen.
    <div className="mx-auto max-w-2xl">
      <div className="mb-3 flex items-end justify-between border-b border-hairline pb-3">
        <h1 className="text-2xl font-semibold text-ink">CDM Calendar</h1>
        <div className="mb-1 flex items-center gap-4">
          {/* Quick way home once you've paged ahead (Charles, 2026-08-05);
              on today there's nothing to do, so it isn't shown. */}
          {date !== today && (
            <button
              onClick={() => pageTo(today)}
              className="tap text-xs font-medium text-navy underline underline-offset-2"
            >
              Today
            </button>
          )}
          <button
            onClick={refresh}
            disabled={refreshing}
            className="tap flex items-center gap-1 text-xs font-medium text-ink underline underline-offset-2 disabled:opacity-50"
          >
            <RefreshCw size={12} className={refreshing ? 'animate-spin' : ''} />
            {refreshing ? 'Checking…' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Day pager — today through the end of the rolling window. */}
      <div className="mb-3 flex items-center justify-between">
        <button
          onClick={() => page(-1)}
          disabled={date <= today}
          aria-label="Previous day"
          className="tap flex items-center justify-center text-navy disabled:opacity-25"
        >
          <ChevronLeft size={22} />
        </button>
        <div className="text-sm font-medium text-ink">
          {fmtDate(date)}
          {date === today && <span className="text-muted"> · Today</span>}
        </div>
        <button
          onClick={() => page(1)}
          disabled={date >= rangeEnd}
          aria-label="Next day"
          className="tap flex items-center justify-center text-navy disabled:opacity-25"
        >
          <ChevronRight size={22} />
        </button>
      </div>

      {/* With no service the phone shows the copy it saved, and says so —
          an empty-looking day is never taken for an empty day (Charles,
          2026-10-14). */}
      <SavedCopyNote
        data={data}
        fetching={isFetching}
        online={online}
        missing={missing}
        missingText="this day wasn’t saved on this phone. The calendar saves the next six days whenever the app is opened with a connection."
        className="mb-3"
      />

      {isLoading ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : missing ? null : !data?.configured ? (
        <p className="py-10 text-center text-sm text-muted">The calendar feed isn’t configured.</p>
      ) : (
        <>
          {data.unreachable && (
            <p className="mb-3 text-sm text-danger">
              Couldn’t reach Google Calendar — try Refresh in a moment.
            </p>
          )}

          <div
            ref={paneRef}
            className="card touch-pan-y overflow-hidden"
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            <div className="relative my-2" style={{ height: totalPx }}>
              {/* Hour lines + gutter labels */}
              {hours.map((h) => (
                <div key={h}>
                  {h > startHour && h < endHour && (
                    <div className="absolute left-12 right-0 border-t border-hairline" style={{ top: y(h * 60) }} />
                  )}
                  {h < endHour && (
                    <div
                      className="absolute left-0 w-11 pr-1.5 text-right text-[10px] tabular-nums text-faint"
                      style={{ top: h === startHour ? 2 : y(h * 60) - 6 }}
                    >
                      {fmtHour(h)}
                    </div>
                  )}
                </div>
              ))}

              {/* Events — overlapping blocks share the width, lane by lane */}
              <div className="absolute inset-y-0 left-12 right-1">
                {events.map((e, i) => (
                  <Block key={i} e={e} y={y} />
                ))}
              </div>

              {showNow && (
                <div className="pointer-events-none absolute left-12 right-0 z-10 border-t-2 border-cyan" style={{ top: y(nowMin) }}>
                  <span className="absolute -left-1 -top-[5px] h-2 w-2 rounded-full bg-cyan" />
                </div>
              )}
            </div>
          </div>

          {events.length === 0 && !data.unreachable && (
            <p className="mt-4 text-center text-sm text-muted">
              No bookings {date === today ? 'on today’s calendar' : `for ${fmtDate(date)}`}.
            </p>
          )}
        </>
      )}
    </div>
  )
}

// Room → palette color (tokens in styles/index.css). Full literal class
// strings on purpose — Tailwind only generates classes it can read.
// A combined booking ("Studio 1/A") wears its first room's color.
const ROOM_CLASSES = {
  A: 'border-room-a/80 bg-room-a/30 [--room:var(--color-room-a)]',
  B: 'border-room-b/80 bg-room-b/30 [--room:var(--color-room-b)]',
  C: 'border-room-c/80 bg-room-c/30 [--room:var(--color-room-c)]',
  1: 'border-room-1/80 bg-room-1/30 [--room:var(--color-room-1)]',
  5: 'border-room-5/80 bg-room-5/30 [--room:var(--color-room-5)]',
}

function Block({ e, y }) {
  const height = Math.max(y(e.e2) - y(e.s), 22)
  const key = (e.room || '').replace('Studio ', '').split('/')[0]
  const colors = ROOM_CLASSES[key] || 'border-cyan/80 bg-cyan/30 [--room:var(--color-cyan)]'
  return (
    <div
      className={`absolute overflow-hidden rounded-md border px-1.5 py-1 ${colors}`}
      style={{
        top: y(e.s),
        height,
        left: `${(e.lane / e.lanes) * 100}%`,
        width: `calc(${100 / e.lanes}% - 2px)`,
      }}
    >
      {/* HOLD leads, then the room, then what's left of the booking name
          (Charles, 2026-08-27). A hold whose title was ONLY the word drops
          the empty segment rather than trailing a dash. */}
      <div className="text-[11px] font-semibold leading-tight text-ink">
        {[e.hold ? 'HOLD' : null, e.room, e.displayTitle ?? e.title].filter(Boolean).join(' - ')}
      </div>
      <div className="mt-0.5 text-[10px] leading-tight text-ink/60">
        {[fmtT(e.start), durText(e.e2 - e.s), e.engineer].filter(Boolean).join(' · ')}
      </div>
    </div>
  )
}

/**
 * Interval layout, the Google way: cluster transitively-overlapping
 * events, assign each a free lane within its cluster, and every block in
 * the cluster splits the width evenly.
 */
function layout(events) {
  const evs = events
    .map((e) => ({ ...e, s: toMin(e.start), e2: toMin(e.end) }))
    .sort((a, b) => a.s - b.s || b.e2 - a.e2)
  let cluster = []
  let clusterEnd = -1
  const closeCluster = () => {
    const laneEnds = []
    for (const ev of cluster) {
      let lane = laneEnds.findIndex((end) => end <= ev.s)
      if (lane === -1) {
        lane = laneEnds.length
        laneEnds.push(0)
      }
      laneEnds[lane] = ev.e2
      ev.lane = lane
    }
    for (const ev of cluster) ev.lanes = laneEnds.length
  }
  for (const ev of evs) {
    if (cluster.length > 0 && ev.s >= clusterEnd) {
      closeCluster()
      cluster = []
      clusterEnd = -1
    }
    cluster.push(ev)
    clusterEnd = Math.max(clusterEnd, ev.e2)
  }
  if (cluster.length > 0) closeCluster()
  return evs
}

function fmtHour(h) {
  return fmtT(`${String(h).padStart(2, '0')}:00`)
}

function durText(mins) {
  const h = Math.floor(mins / 60)
  const m = mins % 60
  if (h === 0) return `${m} min`
  if (m === 0) return `${h} h`
  if (m === 30) return `${h}½ h`
  return `${h}h ${m}m`
}

function fmtDate(d) {
  const [y, m, day] = d.split('-').map(Number)
  return new Date(y, m - 1, day).toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' })
}

const iso = (dt) =>
  `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`

function addDays(d, n) {
  const [y, m, day] = d.split('-').map(Number)
  const dt = new Date(y, m - 1, day)
  dt.setDate(dt.getDate() + n)
  return iso(dt)
}
